import React, { useState } from "react";
import { Box, Button, Typography, Stepper, Step, StepLabel, TextField, FormControlLabel, Checkbox, Radio, RadioGroup, IconButton } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import SensorSelection from "./SensorSelection";
import ActionTypeSelection from "./ActionTypeSelection";
import IconSelector from "../Icon/IconSelector";
import IconColorSelector from "../Icon/IconColorSelector";

const EntityForm = ({ onSave, onCancel, entity }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [entityName, setEntityName] = useState(entity?.name || "");
  const [isGeoLocated, setIsGeoLocated] = useState(entity?.isGeoLocated || false);
  const [hasParentEntity, setHasParentEntity] = useState(entity?.hasParentEntity || false);
  const [geoType, setGeoType] = useState(entity?.geoType || "");
  const [keys, setKeys] = useState(entity?.keys.map((key) => ({ key })) || [{ key: "" }]);
  const [selectedSensors, setSelectedSensors] = useState(entity?.sensors || []);
  const [actionTypes, setActionTypes] = useState(entity?.actions || []);
  const [selectedIcon, setSelectedIcon] = useState(entity?.icon || "Home");
  const [selectedIconColor, setSelectedIconColor] = useState(entity?.iconColor || "#4169E1");

  const isEntityNameValid = entityName.trim() !== "";
  const isSensorStepComplete = selectedSensors.length > 0;
  const isIconSelected = selectedIcon.trim() !== "";

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSave = () => {
    const entityData = {
      _id: entity?._id,
      name: entityName,
      isGeoLocated,
      geoType,
      keys: keys.map((item) => item.key).filter((key) => key.trim() !== ""),
      sensors: selectedSensors,
      actions: actionTypes,
      icon: selectedIcon,
      iconColor: selectedIconColor,
    };
    onSave(entityData);
  };

  const handleAddKey = () => {
    setKeys((prevKeys) => [...prevKeys, { key: "" }]);
  };

  const handleRemoveKey = (index) => {
    setKeys((prevKeys) => prevKeys.filter((_, i) => i !== index));
  };

  const handleKeyChange = (index, value) => {
    setKeys((prevKeys) => prevKeys.map((item, i) => (i === index ? { key: value } : item)));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        maxWidth: 1000,
        width: "100%",
        mx: "auto",
        mt: 4,
        p: 3,
        boxShadow: 3,
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="h6" gutterBottom>
        {entity ? "Edit Entity" : "Create a New Entity"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, textAlign: "center" }}>
        An entity is a blueprint for what needs to be created. Define its name, optional geo-location, and custom keys to structure it.An Entity represents a unit to monitor or manage in various
        scenarios. It can be a vehicle, a traffic intersection, a weather station, etc.
      </Typography>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        <Step>
          <StepLabel>Entity Details</StepLabel>
        </Step>
        <Step>
          <StepLabel>Select Streams</StepLabel>
        </Step>
        <Step>
          <StepLabel>Derived Streams</StepLabel>
        </Step>
        <Step>
          <StepLabel>Select Actions</StepLabel>
        </Step>
        <Step>
          <StepLabel>Select Icon</StepLabel>
        </Step>
        <Step>
          <StepLabel>Icon Color</StepLabel>
        </Step>
      </Stepper>

      {activeStep === 0 && (
        <Box sx={{ width: "100%" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Entity Details
          </Typography>
          <TextField
            label="Entity Name"
            fullWidth
            value={entityName}
            onChange={(e) => setEntityName(e.target.value)}
            helperText="Example: Vehicle, Traffic Intersection, or Weather Station"
            sx={{ mb: 3 }}
          />
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasParentEntity}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setHasParentEntity(checked);

                    // Dynamically update keys or related state if needed
                    if (!checked) {
                      setKeys((prevKeys) => prevKeys.filter((k) => k.key !== "parentEntity"));
                    } else {
                      setKeys((prevKeys) => [...prevKeys, { key: "parentEntity" }]);
                    }
                  }}
                />
              }
              label="Is this entity has parent entity?"
              sx={{ mb: 2 }}
            />
          </div>

          <FormControlLabel control={<Checkbox checked={isGeoLocated} onChange={(e) => setIsGeoLocated(e.target.checked)} />} label="Is this entity geo-located?" sx={{ mb: 2 }} />
          {isGeoLocated && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Select Geo-Location Type:
              </Typography>
              <RadioGroup
                value={geoType}
                onChange={(e) => {
                  const val = e.target.value;
                  // Update keys based on geoType
                  setKeys((prevKeys) => {
                    let updatedKeys = [...prevKeys];

                    if (val === "static") {
                      // Ensure latitude and longitude keys exist
                      if (!updatedKeys.some((k) => k.key === "latitude")) {
                        updatedKeys.push({ key: "latitude" });
                      }
                      if (!updatedKeys.some((k) => k.key === "longitude")) {
                        updatedKeys.push({ key: "longitude" });
                      }
                    } else if (val === "dynamic") {
                      // Remove latitude and longitude keys
                      updatedKeys = updatedKeys.filter((k) => k.key !== "latitude" && k.key !== "longitude");
                    }

                    return updatedKeys;
                  });

                  // Update geoType
                  setGeoType(val);
                }}
                row
              >
                <FormControlLabel value="static" control={<Radio />} label="Static (Fixed Location)" />
                <FormControlLabel value="dynamic" control={<Radio />} label="Dynamic (Requires GPS)" />
              </RadioGroup>
              {geoType === "static" && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  Latitude and Longitude must be provided when creating the entity.
                </Typography>
              )}
              {geoType === "dynamic" && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  A GPS sensor must be attached to this entity for dynamic tracking.
                </Typography>
              )}
            </Box>
          )}
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Custom Keys
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Add custom fields (keys) to uniquely identify or structure this entity. Keys can include identifiers, addresses, etc.
          </Typography>
          {keys.map((item, index) => (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }} key={index}>
              <TextField label={`Key ${index + 1}`} fullWidth value={item.key} onChange={(e) => handleKeyChange(index, e.target.value)} />
              <IconButton onClick={() => handleRemoveKey(index)}>
                <Delete />
              </IconButton>
            </Box>
          ))}
          <Button startIcon={<Add />} variant="outlined" onClick={handleAddKey} sx={{ mb: 3 }}>
            Add Key
          </Button>
          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
          <Button variant="outlined" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleNext} disabled={!isEntityNameValid}>
              Next
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 1 && (
        <Box sx={{ width: "100%" }}>
          <SensorSelection selectedSensors={selectedSensors} setSelectedSensors={setSelectedSensors} derived={false} />
          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
            <Button variant="contained" onClick={handleNext} disabled={!isSensorStepComplete}>
              Next
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 2 && (
        <Box sx={{ width: "100%" }}>
          <SensorSelection selectedSensors={selectedSensors} setSelectedSensors={setSelectedSensors} derived={true} isEntityGeocoded={isGeoLocated} />
          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
            <Button variant="contained" onClick={handleNext} disabled={!isSensorStepComplete}>
              Next
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 3 && (
        <Box sx={{ width: "100%" }}>
          <ActionTypeSelection selectedActions={actionTypes} setSelectedActions={setActionTypes} />
          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 4 && (
        <Box sx={{ width: "100%" }}>
          <IconSelector selectedIcon={selectedIcon} onIconSelect={setSelectedIcon} />
          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
            <Button variant="contained" onClick={handleNext} disabled={!isIconSelected}>
              Next
            </Button>
          </Box>
        </Box>
      )}

      {activeStep === 5 && (
        <Box sx={{ width: "100%" }}>
          <IconColorSelector 
            selectedColor={selectedIconColor} 
            onColorSelect={setSelectedIconColor} 
          />
          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EntityForm;
